import { ContentfulDocumentSchema } from '@ravago/shared/page-data/validators/contentful-document.validator';
import { TechnicalPropertyEntrySchema } from '@ravago/shared/page-data/validators/technical-property.validator';

import { z } from 'zod';

import { TypeProduct } from '../models/contentful-generated';
import { BrandEntrySchema } from './brand.validator';
import { ContentfulAssetSchema } from './contentful-asset.validator';
import { ContentfulSysSchema } from './contentful-sys.validator';
import { FeatureEntrySchema } from './feature.validator';
import { FormEntrySchema } from './form.validator';
import { PolymerSubgroupEntrySchema } from './polymer-subgroup.validator';
import { filterUnresolvedLink, filterUnresolvedLinks } from './preprocessors';
import { ProcessEntrySchema } from './process.validator';
import { ProductFillerEntrySchema } from './product-filler.validator';
import { QualityEntrySchema } from './quality.validator';
import { SegmentEntrySchema } from './segment.validator';
import { SeoMetadataEntrySchema } from './seo-metadata.validator';
import { SpecificationEntrySchema } from './specification.validator';
import { SustainabilitySubgroupEntrySchema } from './sustainability-subgroup.validator';

export const ProductEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: z.looseObject({
    name: z.string(),
    slug: z.string(),
    image: z.preprocess(filterUnresolvedLink, ContentfulAssetSchema.optional().nullable()),
    description: z.union([z.string(), ContentfulDocumentSchema]).optional().nullable(), // Backward compatible: accepts both string (old) and RichText (new)
    brand: BrandEntrySchema,
    ranking: z.number().int().optional().nullable(),
    fillers: z.preprocess(filterUnresolvedLinks, z.array(ProductFillerEntrySchema).optional().nullable()),
    features: z.preprocess(filterUnresolvedLinks, z.array(FeatureEntrySchema).optional().nullable()),
    processes: z.preprocess(filterUnresolvedLinks, z.array(ProcessEntrySchema).optional().nullable()),
    industrySegments: z.preprocess(filterUnresolvedLinks, z.array(SegmentEntrySchema).optional().nullable()),
    specifications: z.preprocess(filterUnresolvedLinks, z.array(SpecificationEntrySchema).optional().nullable()),
    polymerSubgroup: z.preprocess(filterUnresolvedLink, PolymerSubgroupEntrySchema.optional().nullable()),
    ulPortfolio: z.boolean().optional().nullable(),
    ulProductId: z.number().int().optional().nullable(),
    quality: z.preprocess(filterUnresolvedLink, QualityEntrySchema.optional().nullable()),
    grade: z.string().optional().nullable(),
    form: z.preprocess(filterUnresolvedLink, FormEntrySchema.optional().nullable()),
    seoMetadata: z.preprocess(filterUnresolvedLink, SeoMetadataEntrySchema.optional().nullable()),
    preferred: z.boolean().optional().nullable(),
    modifiedAt: z.string().optional().nullable(),
    isPreferred: z.boolean(),
    sustainability: z.preprocess(filterUnresolvedLink, SustainabilitySubgroupEntrySchema.optional().nullable()),
    isTailCatalog: z.boolean().optional().nullable(),
    density: z.preprocess(filterUnresolvedLinks, z.array(TechnicalPropertyEntrySchema).optional().nullable()),
    flowRate: z.preprocess(filterUnresolvedLinks, z.array(TechnicalPropertyEntrySchema).optional().nullable()),
    flameRating: z.preprocess(filterUnresolvedLinks, z.array(TechnicalPropertyEntrySchema).optional().nullable()),
    notchedImpact: z.preprocess(filterUnresolvedLinks, z.array(TechnicalPropertyEntrySchema).optional().nullable()),
    notchedImpactStrength: z.preprocess(filterUnresolvedLinks, z.array(TechnicalPropertyEntrySchema).optional().nullable()),
    isConflicting: z.boolean().optional().nullable()
  }),
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

export function validateProductEntry(data: TypeProduct) {
  return ProductEntrySchema.safeParse(data);
}

/**
 * Returns validation result.
 *
 * @param data
 */
export function validateProductEntries(data: TypeProduct[]) {
  return z.array(ProductEntrySchema).safeParse(data);
}

/**
 * Returns valid entries only. In most cases, this is preferred,
 * It allows us to still show some entries instead of showing nothing when only a single entry is invalid.
 *
 * @param data
 */
export function validateAndFilterProductEntries(data: TypeProduct[]) {
  if (!data) return [];

  return data.reduce((acc: TypeProduct[], entry) => {
    const validationResult = validateProductEntry(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeProduct);
    } else {
      console.warn(
        `Validation failed for product entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}
